import PropTypes from 'prop-types'
import React, { useState, useImperativeHandle, useEffect, useRef } from 'react'
import { Colors } from 'Theme'
import styles from './IFCheckPicker.module.css'
import { IFSkeleton, IFText, IFsvg } from 'Components'
import { CheckPicker } from 'rsuite'
import { getTextWidth } from '../../../Utils/Calculations'

const IFCheckPicker = React.forwardRef(
  (
    {
      title,
      data,
      onValueChange = () => {},
      initialValue,
      isChargingSpeed = false,
      filterRef,
      searchable = false,
      handleSearch = () => {},
      loadMore,
      onClose = () => {},
      onOpen = () => {},
      loading = false,
      paginationOffset,
      firstLoading = false,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [value, setValue] = useState(initialValue)
    const [searchQuery, setSearchQuery] = useState('')
    const [formattedData, setFormattedData] = useState([])
    const [hasScrolled, setHasScrolled] = useState(false)

    const options = []
    for (const index in data) {
      isChargingSpeed
        ? options.push({ label: data[index].label, value: data[index].value })
        : options.push({
            label: data[index],
            value: data[index],
          })
    }
    useEffect(() => {
      if (!searchable) return
      const safeData = Array.isArray(options) ? options : []
      if (
        (loading || firstLoading || safeData.length === 0) &&
        paginationOffset === 0
      ) {
        setFormattedData([
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
        ])
      } else if (paginationOffset !== null && searchQuery.length === 0) {
        setFormattedData([
          ...safeData,
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
        ])
      } else if (loading) {
        setFormattedData([
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
          { label: 'Skeleton', value: 'Skeleton' },
        ])
      } else {
        setFormattedData(safeData)
      }
    }, [loading, searchQuery, data, firstLoading])

    const clearValue = () => {
      setValue([])
    }
    useImperativeHandle(ref, () => ({
      clearValue,
      isOpen,
    }))

    useEffect(() => {
      setValue(initialValue)
    }, [initialValue])

    const pickerRef = useRef()
    useEffect(() => {
      let observer
      let activeEl

      const disableKeys = (e) => {
        const keysToDisable = ['ArrowDown', 'ArrowUp', 'Enter']
        if (keysToDisable.includes(e.key)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      if (isOpen) {
        const attachListener = () => {
          activeEl = document.activeElement
          if (activeEl) {
            activeEl.addEventListener('keydown', disableKeys)
          }
        }
        const timer = setTimeout(() => {
          attachListener()
        }, 0)
        observer = new MutationObserver(() => {
          if (document.activeElement !== activeEl) {
            if (activeEl) {
              activeEl.removeEventListener('keydown', disableKeys)
            }
            attachListener()
          }
        })

        observer.observe(document.body, {
          subtree: true,
          childList: true,
        })

        return () => {
          clearTimeout(timer)
          if (activeEl) {
            activeEl.removeEventListener('keydown', disableKeys)
          }
          observer?.disconnect()
        }
      }
    }, [isOpen])

    const onItemsRendered = (props) => {
      if (!hasScrolled) {
        setHasScrolled(true)
        return
      }

      if (props.visibleStopIndex >= options.length - 1) {
        loadMore()
      }
    }
    const renderMenuItem = (label, item) => (
      <div
        style={{
          pointerEvents: item.value === 'Skeleton' ? 'none' : 'auto',
          backgroundColor:
            item.value === 'Skeleton' ? 'transparent' : undefined,
        }}
      >
        {label === 'Skeleton' ? (
          <div>
            <IFSkeleton variant="text" />
          </div>
        ) : (
          <IFText>{label}</IFText>
        )}
      </div>
    )

    return (
      <div ref={pickerRef} className={styles.wrapperContainer}>
        <div className={styles.container}>
          <CheckPicker
            virtualized={searchable ? true : false}
            ref={filterRef}
            style={{
              width: getTextWidth(title, `600 ${14}px ProximaNova`) + 16,
            }}
            onOpen={() => {
              setIsOpen(true)
              setHasScrolled(false)
              setSearchQuery('')
              onOpen()
            }}
            onClose={() => {
              setIsOpen(false)
              onClose()
            }}
            value={value}
            onSelect={(value) => {
              onValueChange(value)
              setValue(value)
            }}
            container={() => pickerRef?.current}
            data={searchable ? formattedData : options}
            cleanable={false}
            size="lg"
            searchable={searchable}
            placeholder={title}
            renderValue={() => (
              <div>
                <IFText>{title}</IFText>
              </div>
            )}
            className={styles.CheckPicker}
            onSearch={(query) => {
              setSearchQuery(query)
              handleSearch(query)
            }}
            searchBy={
              loading || formattedData.some((item) => item.value === 'Skeleton')
                ? () => true
                : undefined
            }
            listProps={searchable ? { onItemsRendered } : undefined}
            disabledItemValues={['Skeleton']}
            renderMenuItem={searchable ? renderMenuItem : undefined}
          />
          <div
            className={styles.TextWrapper}
            style={{
              backgroundColor: Colors.filterBackground,
            }}
          >
            <IFText className={styles.Text}>
              {/* {Replacing all spaces with &nbsp; for styling} */}
              {title.replaceAll(' ', '\u00a0')}
            </IFText>
            <div>
              <IFsvg.Expand
                height={20}
                width={20}
                fill={Colors.filterIconFilled}
              />
            </div>
          </div>
        </div>
      </div>
    )
  },
)

IFCheckPicker.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  onValueChange: PropTypes.func.isRequired,
  initialValue: PropTypes.array,
  isChargingSpeed: PropTypes.bool,
  filterRef: PropTypes.object,
  searchable: PropTypes.bool,
  handleSearch: PropTypes.func,
  loadMore: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  loading: PropTypes.bool,
  paginationOffset: PropTypes.number,
  firstLoading: PropTypes.bool,
}

export default IFCheckPicker
